import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import axios from '@/utils/axiosInstance';
import { BASE_URL } from '@/config/constants';

export type SectionTextKey = 'toursEvents' | 'showcase' | 'gallery' | 'awards' | 'contact'
  // Management-only sections (siteType 'management' websites)
  | 'services' | 'artistRoster' | 'testimonials' | 'clients' | 'team';

interface SectionTextEntry {
  heading: string;
  subtitle: string;
}

interface SectionTextSettingsCardProps {
  sectionKey: SectionTextKey;
  /** Card heading — defaults to "Section settings" */
  title?: string;
  /** Start expanded (used where this card is the page's primary content) */
  defaultOpen?: boolean;
}

const MAX_HEADING_LENGTH = 80;
const MAX_SUBTITLE_LENGTH = 200;

/**
 * "Section settings" card — heading + subtitle editor for one website section.
 *
 * Prefill contract (same as the Press Kit heading in InfoKitManager): the
 * inputs open filled with the CURRENT effective text (saved value, or the
 * section default), the artist edits in place and saves. Saving an unedited
 * input stores the text explicitly; clearing the subtitle reverts it to the
 * default. The heading is required (Press Kit behaviour).
 *
 * Usage: drop under a manager page's `page-title` header, pass the section key.
 * Renders as a collapsible accordion (collapsed by default; pass `defaultOpen`
 * where it's the page's primary content).
 */
const SectionTextSettingsCard: React.FC<SectionTextSettingsCardProps> = ({
  sectionKey,
  title = 'Section settings',
  defaultOpen = false,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [heading, setHeading] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [defaults, setDefaults] = useState<SectionTextEntry>({ heading: '', subtitle: '' });
  const [saved, setSaved] = useState<SectionTextEntry>({ heading: '', subtitle: '' });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${BASE_URL}/api/section-text`)
      .then((res) => {
        if (cancelled) return;
        const def: SectionTextEntry = {
          heading: res.data?.defaults?.[sectionKey]?.heading || '',
          subtitle: res.data?.defaults?.[sectionKey]?.subtitle || '',
        };
        const entry = res.data?.sections?.[sectionKey] || {};
        // Prefill with the effective text — what the website currently shows.
        const next: SectionTextEntry = {
          heading: entry.heading ?? def.heading,
          subtitle: entry.subtitle ?? def.subtitle,
        };
        setDefaults(def);
        setHeading(next.heading);
        setSubtitle(next.subtitle);
        setSaved(next);
      })
      .catch(() => {
        if (!cancelled) toast.error('Could not load section settings');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sectionKey]);

  const hasChanges = heading.trim() !== saved.heading || subtitle.trim() !== saved.subtitle;

  const handleSave = async () => {
    const trimmedHeading = heading.trim().slice(0, MAX_HEADING_LENGTH);
    const trimmedSubtitle = subtitle.trim().slice(0, MAX_SUBTITLE_LENGTH);
    if (!trimmedHeading) {
      toast.error('Section heading cannot be empty');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${BASE_URL}/api/section-text/${sectionKey}`, {
        heading: trimmedHeading,
        subtitle: trimmedSubtitle,
      });
      // Snap to the effective values so a cleared subtitle repopulates with
      // the default (the same way the initial prefill works).
      const effective = res.data?.effective || { heading: trimmedHeading, subtitle: trimmedSubtitle };
      const next: SectionTextEntry = {
        heading: effective.heading ?? '',
        subtitle: effective.subtitle ?? '',
      };
      setHeading(next.heading);
      setSubtitle(next.subtitle);
      setSaved(next);
      toast.success('Section settings saved');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to save section settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setHeading(saved.heading);
    setSubtitle(saved.subtitle);
  };

  // Blank fields mean "use the default" — drives the header badge.
  const isDefault =
    (heading.trim() || defaults.heading) === defaults.heading &&
    (subtitle.trim() || defaults.subtitle) === defaults.subtitle;

  return (
    <div className="border border-border rounded-xl overflow-hidden mb-4">
      <button
        type="button"
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium bg-muted/40 hover:bg-muted/60 transition-colors"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="flex items-center gap-2">
          {title}
          {!loading && (
            <span className={`text-[10px] font-mono ${isDefault ? 'text-muted-foreground' : 'text-primary font-semibold'}`}>
              {isDefault ? 'Default' : 'Custom'}
            </span>
          )}
        </span>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {open && (
        <div className="px-4 py-4 border-t border-border/50">
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading section settings…</p>
          ) : (
            <div className="space-y-5 max-w-xl">
              <div className="form-group">
                <Label className="form-label">
                  Section Heading<span className="text-red-500">*</span>
                </Label>
                <Input
                  name="heading"
                  value={heading}
                  maxLength={MAX_HEADING_LENGTH}
                  onChange={(e) => setHeading(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Shown as the section title{defaults.heading ? ` — defaults to “${defaults.heading}”` : ''}. Customising it
                  also updates the website navigation label.
                </p>
              </div>

              <div className="form-group">
                <Label className="form-label">Section Subtitle</Label>
                <Input
                  name="subtitle"
                  value={subtitle}
                  maxLength={MAX_SUBTITLE_LENGTH}
                  onChange={(e) => setSubtitle(e.target.value)}
                  placeholder={defaults.subtitle || 'Optional'}
                />
                <p className="text-xs text-muted-foreground">
                  Optional line under the heading. Leave blank to use the default.
                </p>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <Button type="button" loading={saving} disabled={!hasChanges} onClick={handleSave}>
                  Save Settings
                </Button>
                <Button type="button" variant="tertiary" disabled={!hasChanges || saving} onClick={handleReset}>
                  Reset
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SectionTextSettingsCard;
